/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment-timezone';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, LockOutlined } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useConfirm } from 'material-ui-confirm';
import { createEmptyTask, MEDIA_TYPES } from '../Task.model';
import {
  TASK_VALIDATION_MODES,
  TITLE_MAX_LENGTH,
  validateTask,
} from '../Task.validation';
import colors from '../../colors.styles';
import adminFormStyles from '../../shared/styles/adminForm.styles';
import TaskContentRequestsForm from './TaskContentRequestsForm';
import TargetingForm from '../../shared/components/TargetingForm';

const styles = {
  ...adminFormStyles,
  backIcon: {
    fontSize: '14px !important',
  },
  endedChip: {
    backgroundColor: colors.base.very_light_gray,
    color: colors.base.dark_gray,
    fontWeight: 600,
  },
  endedAlert: {
    alignItems: 'flex-start',
    backgroundColor: '#f6f6f6',
    border: '1px solid #e9ecee',
    borderRadius: '12px',
    color: '#656d80',
    fontSize: '14px',
    py: 0.5,
    '& .MuiAlert-icon': { color: '#656d80', mt: 0.25 },
  },
};

// Content item errors are keyed by ui_id, not by position.
const NO_ERRORS = { content_requests: {} };

// Compare day-level dates and item fields only (ignore ui_id).
function hasTaskChanges(task, baseline) {
  const dueDatesMatch =
    (!task.due_date && !baseline.due_date) ||
    (task.due_date &&
      baseline.due_date &&
      moment(task.due_date).isSame(moment(baseline.due_date), 'day'));

  const normalizeContentRequests = (contentRequests) =>
    (contentRequests || []).map(({ id, title, description, media_type }) => ({
      id: id ?? null,
      title,
      description,
      media_type,
    }));

  return (
    task.title !== baseline.title ||
    task.description !== baseline.description ||
    !dueDatesMatch ||
    !_.isEqual(
      normalizeContentRequests(task.content_requests),
      normalizeContentRequests(baseline.content_requests)
    )
  );
}

export default function TaskForm({
  initialTask,
  isEnding,
  isSaving,
  onBack,
  onEndNow,
  onSaveDraft,
  onLaunch,
}) {
  const confirm = useConfirm();
  const [task, setTask] = useState(() => initialTask || createEmptyTask());
  const [validationErrors, setValidationErrors] = useState(NO_ERRORS);
  const [isTargetingValid, setIsTargetingValid] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const isEditingLaunchedTask = Boolean(task.task_id && task.launch_date);
  // A launched request ends when "End now" sets ended_at, or when its
  // deadline passes. Either way it drops out of the app's task
  // query. A draft with a stale deadline was never live, so it isn't "ended"
  // and stays editable. Read from the persisted initialTask, not the
  // in-progress edit, so dragging the deadline picker around doesn't flip
  // this before saving.
  const isEnded = Boolean(
    initialTask?.task_id &&
      initialTask?.launch_date &&
      (initialTask?.ended_at ||
        (initialTask?.due_date &&
          moment(initialTask.due_date).isSameOrBefore()))
  );
  const showEndedAt = Boolean(isEnded && initialTask?.ended_at);

  useEffect(() => {
    setTask(initialTask || createEmptyTask());
    setValidationErrors(NO_ERRORS);
    setIsTargetingValid(true);
    setIsValidating(false);
  }, [initialTask]);

  function updateTask(field, value) {
    setTask((currentTask) => ({
      ...currentTask,
      [field]: value,
    }));
  }

  function handleTargetingChanged(nextTargeting, isValid) {
    updateTask('targeting', nextTargeting);
    setIsTargetingValid(isValid);
  }

  function updateContentRequests(contentRequests) {
    updateTask('content_requests', contentRequests);
    dropErrorsForRemovedContentRequests(contentRequests);
  }

  function dropErrorsForRemovedContentRequests(contentRequests) {
    setValidationErrors((currentErrors) => {
      const remainingErrors = _.pick(
        currentErrors.content_requests,
        _.map(contentRequests, 'ui_id')
      );

      if (_.size(remainingErrors) === _.size(currentErrors.content_requests)) {
        return currentErrors;
      }

      return {
        ...currentErrors,
        content_requests: remainingErrors,
        // Removing the last incomplete item resolves the summary too.
        ...(_.isEmpty(remainingErrors)
          ? { content_requests_summary: undefined }
          : {}),
      };
    });
  }

  function clearValidationError(field) {
    setValidationErrors((currentErrors) => {
      if (!currentErrors[field]) return currentErrors;

      return {
        ...currentErrors,
        [field]: undefined,
      };
    });
  }

  function submit(mode, callback) {
    // An ended request is read-only — nothing can be saved to it.
    if (isEnded) return;

    setIsValidating(true);
    const result = validateTask(task, mode);
    setValidationErrors(result.errors);

    // Invalid targeting only blocks a launch; a draft can be saved with it
    // (the API trims unusable values on the way in).
    const targetingBlocksLaunch =
      mode === TASK_VALIDATION_MODES.LAUNCH && !isTargetingValid;

    if (result.isValid && !targetingBlocksLaunch) {
      callback(task);
    } else if (!result.isValid) {
      focusFirstError(result.errors);
    }
  }

  // Send the admin straight to the first field that needs attention, top to
  // bottom, so a failed "Save as draft"/"Post request" doesn't just silently
  // paint errors somewhere off screen.
  function focusFirstError(errors) {
    const firstInvalidContentRequest = task.content_requests.find(
      (contentRequest) => errors.content_requests?.[contentRequest.ui_id]
    );

    const targetId =
      (errors.title && 'task-title') ||
      (errors.description && 'task-description') ||
      (errors.due_date && 'task-deadline') ||
      (firstInvalidContentRequest &&
        `content-request-title-${firstInvalidContentRequest.ui_id}`) ||
      (errors.content_requests_summary && 'task-content-requests');

    if (!targetId) return;

    // Wait for the error helper text to render so the field lands in view
    // with its message, not just above it.
    requestAnimationFrame(() => {
      const element = document.getElementById(targetId);
      if (!element) return;

      if (typeof element.scrollIntoView === 'function') {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      element.focus({ preventScroll: true });
    });
  }

  function saveDraft() {
    submit(TASK_VALIDATION_MODES.DRAFT, onSaveDraft);
  }

  function launchTask() {
    submit(TASK_VALIDATION_MODES.LAUNCH, onLaunch);
  }

  async function endNow() {
    try {
      await confirm({
        title: 'End this request now?',
        description:
          "This will end the request immediately and remove it from associates' apps. This can't be undone.",
        confirmationText: 'Yes, end now',
        cancellationText: 'Cancel',
        confirmationButtonProps: { color: 'error' },
      });
    } catch {
      // Cancelled.
      return;
    }

    onEndNow(task);
  }

  async function handleBack() {
    const baseline = initialTask || createEmptyTask();
    if (!hasTaskChanges(task, baseline)) {
      onBack();
      return;
    }

    // A live request can only be saved through the review-and-confirm
    // screen, never silently — so back navigation here only offers to
    // discard, not to save.
    if (isEditingLaunchedTask) {
      try {
        await confirm({
          title: 'Leave without updating?',
          description:
            'You have unsaved changes to this live request. Use "Update request" to save them, or leave to discard your edits.',
          confirmationText: 'Leave without saving',
          cancellationText: 'Keep editing',
        });
      } catch {
        // "Keep editing" (confirm rejects on cancel).
        return;
      }

      onBack();
      return;
    }

    try {
      await confirm({
        title: 'Save your changes?',
        description:
          'You have unsaved changes. Would you like to save before leaving?',
        confirmationText: 'Save',
        cancellationText: "Don't save",
      });
    } catch {
      // "Don't save" (confirm rejects on cancel).
      onBack();
      return;
    }

    const result = validateTask(task, TASK_VALIDATION_MODES.DRAFT);
    setValidationErrors(result.errors);
    if (!result.isValid) return;

    await onSaveDraft(task);
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Stack spacing="20px">
        <Box>
          <Button
            color="inherit"
            disableRipple
            startIcon={<ArrowBack sx={styles.backIcon} />}
            size="small"
            sx={styles.backButton}
            onClick={handleBack}
          >
            Content requests
          </Button>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography component="h1" sx={styles.pageTitle}>
                {/* eslint-disable-next-line no-nested-ternary */}
                {isEditingLaunchedTask
                  ? 'Update Content Request'
                  : task.task_id
                  ? 'Edit Content Request'
                  : 'Create Content Request'}
              </Typography>
              {isEnded && (
                <Chip label="Ended" size="small" sx={styles.endedChip} />
              )}
            </Stack>
            {!isEditingLaunchedTask && (
              <Button
                type="button"
                variant="outlined"
                onClick={saveDraft}
                disabled={isSaving}
                startIcon={
                  isSaving ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null
                }
                sx={styles.secondaryButton}
              >
                {isSaving ? 'Saving...' : 'Save as draft'}
              </Button>
            )}
            {isEditingLaunchedTask && !isEnded && (
              <Button
                type="button"
                variant="outlined"
                color="error"
                onClick={endNow}
                disabled={isEnding}
                startIcon={
                  isEnding ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null
                }
              >
                {isEnding ? 'Ending...' : 'End now'}
              </Button>
            )}
          </Stack>
        </Box>

        {isEnded && (
          <Alert
            icon={<LockOutlined fontSize="small" />}
            severity="info"
            sx={styles.endedAlert}
          >
            <strong>This request has ended.</strong> It can no longer be edited.
          </Alert>
        )}

        <Paper component="section" variant="outlined" sx={styles.section}>
          <Stack spacing="20px" padding="20px">
            <Box>
              <Typography component="h2" sx={styles.sectionTitle}>
                Request details
              </Typography>
              <Typography sx={styles.sectionDescription}>
                This is what associates see at the top of the task.
              </Typography>
            </Box>

            <Stack spacing={0.75}>
              <FormLabel htmlFor="task-title" sx={styles.fieldLabel}>
                Title
              </FormLabel>
              <TextField
                fullWidth
                disabled={isEnded}
                id="task-title"
                placeholder="e.g. Valentine’s Day Campaign"
                value={task.title}
                onChange={(event) => {
                  const { value } = event.target;
                  updateTask('title', value);
                  if (value.trim()) clearValidationError('title');
                }}
                error={Boolean(validationErrors.title)}
                helperText={validationErrors.title}
                inputProps={{ maxLength: TITLE_MAX_LENGTH }}
                sx={styles.field}
              />
            </Stack>

            <Stack spacing={0.75}>
              <FormLabel htmlFor="task-description" sx={styles.fieldLabel}>
                Description
              </FormLabel>
              <TextField
                fullWidth
                disabled={isEnded}
                id="task-description"
                multiline
                minRows={4}
                placeholder="e.g. Help us promote Valentine's day on our Brand Social pages! We'll select a few photos to use across our channels - so keep an eye on our FB, IG, and TT to see if your submission is featured!"
                value={task.description}
                onChange={(event) =>
                  updateTask('description', event.target.value)
                }
                error={Boolean(validationErrors.description)}
                helperText={validationErrors.description}
                sx={styles.field}
              />
              <Typography sx={styles.helperText}>
                Plain language, sentence case.
              </Typography>
            </Stack>

            <Stack spacing={0.75}>
              <FormLabel htmlFor="task-deadline" sx={styles.fieldLabel}>
                Deadline
              </FormLabel>
              <DatePicker
                disabled={isEnded}
                format={
                  showEndedAt
                    ? `MM/DD/YYYY [${moment(initialTask.ended_at)
                        .tz('America/Chicago')
                        .format('h:mm A')}]`
                    : 'MM/DD/YYYY 11:59 P[M]'
                }
                // Show ended_at in CST when the request was ended before the due date.
                value={(showEndedAt
                  ? moment(initialTask.ended_at)
                  : moment(task.due_date)
                ).tz('America/Chicago')}
                onChange={(value) => {
                  // Set the due date/time to 11:59 PM on the selected day in CST
                  const dueDate = moment.tz(
                    {
                      year: moment(value).year(),
                      month: moment(value).month(),
                      date: moment(value).date(),
                      hour: 23,
                      minute: 59,
                    },
                    'America/Chicago'
                  );
                  updateTask('due_date', dueDate);
                }}
                slotProps={{
                  textField: {
                    id: 'task-deadline',
                    error: Boolean(validationErrors.due_date),
                    helperText: validationErrors.due_date,
                    placeholder: 'MM/DD/YYYY',
                  },
                }}
                sx={{ ...styles.field, maxWidth: '300px' }}
              />
              <Typography sx={styles.helperText}>
                11:59 PM CST on the date selected. One deadline applies to the
                whole request.
              </Typography>
            </Stack>
          </Stack>
        </Paper>

        <TaskContentRequestsForm
          contentRequests={task.content_requests}
          errors={validationErrors.content_requests}
          isLaunched={isEditingLaunchedTask}
          isReadOnly={isEnded}
          onChange={updateContentRequests}
          summaryError={validationErrors.content_requests_summary}
        />

        <Paper component="section" variant="outlined" sx={styles.section}>
          <Stack spacing="20px" padding="20px">
            <TargetingForm
              key={initialTask?.task_id || 'new'}
              initialTargeting={initialTask?.targeting}
              isReadOnly={isEditingLaunchedTask || isEnded}
              isValidating={isValidating}
              onStateChanged={handleTargetingChanged}
              savedReach={initialTask?.reach}
              subject="request"
            />
          </Stack>
        </Paper>

        {!isEnded && (
          <Paper component="footer" variant="outlined" sx={styles.section}>
            <Stack
              direction="row"
              spacing={1.5}
              justifyContent="flex-end"
              padding="16px 20px"
            >
              {!isEditingLaunchedTask && (
                <Button
                  type="button"
                  variant="outlined"
                  onClick={saveDraft}
                  disabled={isSaving}
                  startIcon={
                    isSaving ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : null
                  }
                  sx={styles.secondaryButton}
                >
                  {isSaving ? 'Saving...' : 'Save as draft'}
                </Button>
              )}
              <Button
                type="button"
                variant="contained"
                onClick={launchTask}
                disabled={isSaving}
                sx={styles.primaryButton}
              >
                {isEditingLaunchedTask ? 'Update request' : 'Post request'}
              </Button>
            </Stack>
          </Paper>
        )}
      </Stack>
    </LocalizationProvider>
  );
}

TaskForm.propTypes = {
  initialTask: PropTypes.shape({
    task_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    task_type: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    targeting: PropTypes.shape({
      store_types: PropTypes.arrayOf(PropTypes.string),
      states: PropTypes.arrayOf(PropTypes.string),
      regions: PropTypes.arrayOf(PropTypes.string),
      markets: PropTypes.arrayOf(PropTypes.string),
      store_numbers: PropTypes.arrayOf(PropTypes.string),
      employee_types: PropTypes.arrayOf(PropTypes.string),
      specific_users: PropTypes.arrayOf(PropTypes.string),
    }),
    reach: PropTypes.shape({
      store_count: PropTypes.number,
      user_count: PropTypes.number,
    }),
    launch_date: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
    due_date: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
    ended_at: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
    launched_by: PropTypes.string,
    content_requests: PropTypes.arrayOf(
      PropTypes.shape({
        ui_id: PropTypes.string.isRequired,
        id: PropTypes.number,
        title: PropTypes.string,
        description: PropTypes.string,
        media_type: PropTypes.oneOf(['', ...Object.values(MEDIA_TYPES)]),
      })
    ),
  }),
  isEnding: PropTypes.bool,
  isSaving: PropTypes.bool,
  onBack: PropTypes.func,
  onEndNow: PropTypes.func,
  onSaveDraft: PropTypes.func,
  onLaunch: PropTypes.func,
};

TaskForm.defaultProps = {
  initialTask: null,
  isEnding: false,
  isSaving: false,
  onBack: () => {},
  onEndNow: () => {},
  onSaveDraft: () => {},
  onLaunch: () => {},
};
